import React, {useState, useEffect} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Pagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as TransferIcon,
  Search as SearchIcon,
  VerifiedUser as ValidateIcon,
  CheckCircle,
  Error,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {httpRequest} from '../../@crema/services/auth/jwt-auth';

const InvalidDataList = () => {
  const [invalidData, setInvalidData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    reason: '',
    source: '',
    data_source: '',
    date_from: '',
    date_to: '',
    age_min: '',
    age_max: '',
    country: '',
    funnel_name: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  });
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [validationResult, setValidationResult] = useState({
    isValid: false,
    errors: [],
  });
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [transferringItemId, setTransferringItemId] = useState(null);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [transferItem, setTransferItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [validatingItemId, setValidatingItemId] = useState(null); // used to show spinner on validate icon
  const [validationPhase, setValidationPhase] = useState('idle'); // idle | validating | checking_duplicate | done
  const [selectedItems, setSelectedItems] = useState([]); // for bulk operations
  const [bulkTransferring, setBulkTransferring] = useState(false); // for bulk transfer loading state
  const [validationErrors, setValidationErrors] = useState([]); // for storing validation errors
  const [openBulkTransferModal, setOpenBulkTransferModal] = useState(false); // for bulk transfer confirmation
  const [openOtherDataModal, setOpenOtherDataModal] = useState(false); // for showing other_data
  const [selectedOtherData, setSelectedOtherData] = useState(null); // for storing selected other_data
  
  // Dynamic filter options from backend
  const [filterOptions, setFilterOptions] = useState({
    reasons: [],
    sources: [],
    dataSources: []
  });

  useEffect(() => {
    fetchInvalidData(false, 0, pagination.pageSize);
    // Fetch filter options on mount
    fetchFilterOptions();
    // Intentionally empty dependency array - only run on mount
  }, []);

  // Fetch filter options after data is loaded (as fallback)
  useEffect(() => {
    if (invalidData.length > 0 && filterOptions.sources.length === 0 && filterOptions.reasons.length === 0) {
      // Only fetch if we don't have filter options yet
      fetchFilterOptions();
    }
  }, [invalidData, filterOptions.sources.length, filterOptions.reasons.length]);

  // Debounced search - 300ms delay without loading labels
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  const fetchInvalidData = async (
    isRefresh = false,
    page = 0,
    pageSize = 10,
    filterParams = {},
  ) => {

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Always include pagination parameters to ensure backend uses paginated method
      const queryParams = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: pageSize.toString(),
        ...filterParams,
      });

      // Remove empty parameters (but keep pagination parameters)
      Object.keys(queryParams).forEach((key) => {
        if (
          key !== 'pageNumber' &&
          key !== 'pageSize' &&
          (!queryParams.get(key) || queryParams.get(key) === '')
        ) {
          queryParams.delete(key);
        }
      });

      const response = await httpRequest(
        `invalid-data?${queryParams.toString()}`,
        {
          method: 'GET',
        },
      );

      const responseData = response.data.data || {};
      console.log('🔧 DEBUG: Server response:', responseData);

      // Handle both paginated and non-paginated responses
      if (Array.isArray(responseData)) {
        // Legacy response format (array of data)
        setInvalidData(responseData);
        setPagination({
          page: 0,
          pageSize: responseData.length,
          total: responseData.length,
          totalPages: 1,
        });
      } else {
        // New paginated response format
        setInvalidData(responseData.data || []);
        setPagination({
          page: responseData.page || 0,
          pageSize: responseData.pageSize || 10,
          total:
            responseData.total ||
            (responseData.data ? responseData.data.length : 0),
          totalPages: responseData.totalPages || 1,
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching invalid data with filters:', err);

      // Fallback to original endpoint if filtering fails
      try {
        console.log('🔄 Fallback: Trying original endpoint...');
        const fallbackResponse = await httpRequest('invalid-data', {
          method: 'GET',
        });

        const fallbackData = fallbackResponse.data.data || [];
        setInvalidData(fallbackData);
        setPagination({
          page: 0,
          pageSize: fallbackData.length,
          total: fallbackData.length,
          totalPages: 1,
        });
        setError(null);
        console.log(
          '✅ Fallback successful, loaded',
          fallbackData.length,
          'items',
        );
      } catch (fallbackErr) {
        setError('Failed to fetch invalid data');
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  const fetchFilterOptions = async () => {
    try {
      console.log('🔍 Fetching filter options...');
      const response = await httpRequest('invalid-data/filter-options', {method: 'GET'});
      console.log('🔍 Filter options response:', response);
      
      // Handle different response structures
      let filterData = null;
      if (response.data?.data) {
        // Backend returns { data: { reasons, sources, dataSources }, status: 200 }
        filterData = response.data.data;
      } else if (response.data) {
        // Direct data structure or alternative structure
        filterData = response.data;
      }
      
      if (filterData) {
        console.log('✅ Filter options received:', filterData);
        setFilterOptions({
          reasons: filterData.reasons || [],
          sources: filterData.sources || [],
          dataSources: filterData.dataSources || []
        });
      } else {
        console.warn('⚠️ No filter data in response, using fallback');
        // Fallback: use data from current page to populate filters
        const currentReasons = [...new Set(invalidData.map(item => formatErrorReason(item)))];
        const currentSources = [...new Set(invalidData.map(item => item.integration_name).filter(Boolean))];
        const currentDataSources = [...new Set(invalidData.map(item => item.raw_data?.data_source).filter(Boolean))];
        
        setFilterOptions({
          reasons: currentReasons,
          sources: currentSources,
          dataSources: currentDataSources
        });
      }
    } catch (err) {
      console.error('❌ Error fetching filter options:', err);
      // Fallback: use data from current page to populate filters
      const currentReasons = [...new Set(invalidData.map(item => formatErrorReason(item)))];
      const currentSources = [...new Set(invalidData.map(item => item.integration_name).filter(Boolean))];
      const currentDataSources = [...new Set(invalidData.map(item => item.raw_data?.data_source).filter(Boolean))];
      
      setFilterOptions({
        reasons: currentReasons,
        sources: currentSources,
        dataSources: currentDataSources
      });
    }
  };

  const applyFilters = async () => {
    setLoadingFilters(true);
    try {
      // Build filter parameters for server request
      const filterParams = {};

      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          // Accept YYYY-MM-DD or Date/string
          const dd = d instanceof Date ? new Date(d) : new Date(`${d}T00:00:00`);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };

      if (filters.search) {
        filterParams.search = filters.search;
      }
      if (filters.reason) {
        filterParams.reason = filters.reason;
      }
      if (filters.source) {
        filterParams.source = filters.source;
      }
      if (filters.data_source) {
        filterParams.data_source = filters.data_source;
      }
      if (filters.date_from || filters.date_to) {
        const fromIso = normalizeToDayIso(filters.date_from, false);
        const toIso = normalizeToDayIso(filters.date_to, true);
        if (fromIso) filterParams.date_from = fromIso;
        if (toIso) filterParams.date_to = toIso;
      }
      if (filters.age_min) {
        filterParams.age_min = filters.age_min;
      }
      if (filters.age_max) {
        filterParams.age_max = filters.age_max;
      }
      if (filters.country) {
        filterParams.country = filters.country;
      }
      if (filters.funnel_name) {
        filterParams.funnel_name = filters.funnel_name;
      }

      // Reset to first page when applying filters
      await fetchInvalidData(false, 0, pagination.pageSize, filterParams);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const rawData = item.raw_data || {};
    setFormData({
      name: rawData.fullName || rawData.name || '',
      phone: rawData.phone || '',
      age: rawData.age || '100', // Default age to 100 if not provided
      email: rawData.email || '',
      country: rawData.country || '',
      marks: rawData.marks || '',
      MPC_4: rawData.MPC_4 || '',
      data_source: rawData.data_source || '',
      other_data:
        typeof rawData.other_data === 'object'
          ? JSON.stringify(rawData.other_data, null, 2)
          : rawData.other_data || '',
    });
    setOpenEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      setUpdatingItemId(editingItem.id);
      setError(null);

      // Prepare data for submission - parse other_data if it's JSON
      const dataToSend = {...formData};

      // Map name to fullName for backend compatibility
      if (dataToSend.name) {
        dataToSend.fullName = dataToSend.name;
        delete dataToSend.name;
      }

      if (dataToSend.other_data) {
        try {
          // Try to parse as JSON if it looks like JSON
          if (
            dataToSend.other_data.trim().startsWith('{') ||
            dataToSend.other_data.trim().startsWith('[')
          ) {
            dataToSend.other_data = JSON.parse(dataToSend.other_data);
          }
        } catch (e) {
          // If parsing fails, keep it as string
          console.log('other_data is not JSON, keeping as string');
        }
      }

      const resp = await httpRequest(`invalid-data/${editingItem.id}`, {
        method: 'PUT',
        body: dataToSend,
      });

      // Prefer server-updated record (backend writes duplicate status to error_message)
      let updatedRow = resp?.data?.data || null;

      // Ensure integration_name is preserved from the original item
      if (updatedRow && editingItem.integration_name) {
        updatedRow.integration_name = editingItem.integration_name;
      }

      // Immediately ask backend to validate this draft to compute duplicate state
      try {
        const validateResp = await httpRequest(
          `invalid-data/${editingItem.id}/validate`,
          {
            method: 'POST',
            body: dataToSend,
          },
        );
        const vr = validateResp?.data?.data || {};
        let message = '';
        if (!vr.isValid) {
          message = (vr.errors && vr.errors.join('; ')) || 'Validation failed';
        } else if (vr.duplicateStatus === 'duplicate_free') {
          message = 'duplicate_free';
        } else if (vr.duplicateStatus === 'duplicate_sent') {
          message = 'duplicate_sent';
        } else {
          message = '✅ Data validated successfully - Ready for transfer';
        }
        const base = updatedRow || editingItem;
        updatedRow = {
          ...base,
          error_message: message,
          raw_data: {...(base.raw_data || {}), ...dataToSend},
          // Preserve the integration_name field
          integration_name:
            base.integration_name || editingItem.integration_name,
        };
      } catch (e) {
        // Fallback to server response or full refresh
        if (!updatedRow) {
          await fetchInvalidData(true);
        }
      }

      if (updatedRow) {
        setInvalidData((prevData) =>
          prevData.map((item) =>
            item.id === editingItem.id ? updatedRow : item,
          ),
        );
      }

      setOpenEditDialog(false);
      setEditingItem(null);
      setFormData({});

      setSuccessMessage('Invalid data updated. Status refreshed.');
      setOpenSuccessModal(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to update invalid data';
      setError(`Update failed: ${errorMessage}`);
      console.error('Error updating invalid data:', err);
    } finally {
      setUpdatingItemId(null);
    }
  };

  // Individual item selection handler

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const isItemSelected = (itemId) => selectedItems.includes(itemId);

  const canItemBeTransferred = (item) => {
    return (
      item.error_message?.includes('✅ Data validated successfully') ||
      item.error_message?.includes(
        'Data has been fixed and is ready for transfer',
      ) ||
      item.error_message?.includes(
        'Data has been validated and is ready for transfer',
      ) ||
      item.error_message === 'unique'
    );
  };

  // Show bulk transfer confirmation modal
  const handleBulkTransferClick = () => {
    if (selectedItems.length === 0) return;
    setOpenBulkTransferModal(true);
  };

  // Confirm bulk transfer
  const confirmBulkTransfer = async () => {
    setOpenBulkTransferModal(false);
    await handleBulkTransfer();
  };

  // Bulk transfer function
  const handleBulkTransfer = async () => {
    if (selectedItems.length === 0) return;

    try {
      setBulkTransferring(true);
      setError(null);

      const response = await httpRequest('invalid-data/bulk-transfer', {
        method: 'POST',
        body: {ids: selectedItems},
      });

      // Check if the response is successful (status 200)
      if (response.status === 200 && response.data.status === 200) {
        const transferredCount = response.data.data?.transferredCount || 0;
        const failedCount = response.data.data?.failedCount || 0;

        if (transferredCount > 0) {
          setSuccessMessage(
            `Successfully transferred ${transferredCount} items to main data table${
              failedCount > 0 ? ` (${failedCount} failed)` : ''
            }`,
          );
          setOpenSuccessModal(true);

          // Remove transferred items from the list
          setInvalidData((prevData) =>
            prevData.filter((item) => !selectedItems.includes(item.id)),
          );
          setSelectedItems([]);
        } else {
          setError('No items were transferred');
        }
      } else {
        // Check if there are validation errors
        if (
          response.data.data?.validationErrors &&
          response.data.data.validationErrors.length > 0
        ) {
          setValidationErrors(response.data.data.validationErrors);
          setOpenValidationModal(true);
        } else {
          setError(
            `Bulk transfer failed: ${response.data.message || 'Unknown error'}`,
          );
        }
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to transfer items';
      setError(`Bulk transfer failed: ${errorMessage}`);
      console.error('Error in bulk transfer:', err);
    } finally {
      setBulkTransferring(false);
    }
  };

  const validateData = (item) => {
    const rawData = item.raw_data || {};
    const errors = [];
    const warnings = [];

    // Check required fields with enhanced messages
    if (!rawData.fullName && !rawData.name) {
      errors.push(
        "❌ Full Name is required - Please enter the customer's complete name",
      );
    }

    if (!rawData.email) {
      errors.push(
        '❌ Email Address is required - Please enter a valid email address',
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawData.email)) {
      errors.push(
        '❌ Invalid Email Format - Please enter a valid email (e.g., user@example.com)',
      );
    }

    if (!rawData.phone) {
      errors.push(
        '❌ Phone Number is required - Please enter a valid phone number',
      );
    } else {
      const cleanPhone = rawData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 7) {
        errors.push(
          '❌ Phone Number Too Short - Must be at least 7 digits (e.g., 1234567)',
        );
      } else if (cleanPhone.length > 15) {
        errors.push(
          '❌ Phone Number Too Long - Must be no more than 15 digits',
        );
      } else if (!/^[1-9]\d{6,14}$/.test(cleanPhone)) {
        errors.push(
          '❌ Invalid Phone Format - Must start with 1-9, no + prefix allowed',
        );
      } else if (rawData.phone.includes('+')) {
        errors.push(
          '❌ Remove + Prefix - Phone numbers should not include + symbol',
        );
      }
    }

    if (!rawData.country) {
      errors.push('❌ Country is required - Please enter a country name');
    }

    // Enhanced age validation
    if (!rawData.age) {
      errors.push("❌ Age is required - Please enter the customer's age");
    } else {
      const age = parseInt(rawData.age);
      if (isNaN(age)) {
        errors.push(
          '❌ Invalid Age Format - Please enter a valid number (e.g., 25)',
        );
      } else if (age < 0) {
        errors.push('❌ Invalid Age - Age cannot be negative');
      } else if (age > 120) {
        warnings.push('⚠️ Unusual Age - Please verify this age is correct');
      } else if (age < 18) {
        warnings.push(
          '⚠️ Minor Customer - Age is under 18, ensure compliance with regulations',
        );
      }
    }

    // Additional field validations
    if (!rawData.marks || String(rawData.marks).trim() === '') {
      errors.push('❌ Marks is required - Please enter a brief description');
    }

    if (!rawData.MPC_4) {
      errors.push(
        '❌ Funnel Name is required - Please enter the funnel/source where this lead came from',
      );
    }

    // Combine errors and warnings
    const allIssues = [...errors, ...warnings];

    return {
      isValid: errors.length === 0,
      errors: errors,
      warnings: warnings,
      allIssues: allIssues,
    };
  };

  const handleValidate = async (item) => {
    try {
      setValidatingItemId(item.id);
      setValidationPhase('validating');
      setOpenValidationModal(true); // open immediately with loading
      const resp = await httpRequest(`invalid-data/${item.id}/validate`, {
        method: 'POST',
        body: {},
      });
      setValidationPhase('checking_duplicate');
      const result = resp?.data?.data || {};

      // Update chip status based on server
      let message = '';
      if (!result.isValid) {
        message =
          (result.errors && result.errors.join('; ')) || 'Validation failed';
      } else if (result.duplicateStatus === 'duplicate_free') {
        message = 'duplicate_free';
      } else if (result.duplicateStatus === 'duplicate_sent') {
        message = 'duplicate_sent';
      } else {
        message = '✅ Data validated successfully - Ready for transfer';
      }
      setInvalidData((prev) =>
        prev.map((row) =>
          row.id === item.id ? {...row, error_message: message} : row,
        ),
      );

      setValidationResult({
        isValid: !!result.isValid,
        errors: result.errors || [],
        warnings: result.warnings || [],
      });
      setValidationPhase('done');
    } catch (err) {
      console.error('Server validation failed:', err);
      setError('Validation failed. Please try again.');
    } finally {
      setValidatingItemId(null);
    }
  };

  const handleTransfer = async (id) => {
    const item = invalidData.find((item) => item.id === id);
    if (!item) return;

    // First validate the data
    const validation = validateData(item);
    if (!validation.isValid) {
      setValidationResult(validation);
      setOpenValidationModal(true);
      return;
    }

    // Show confirmation modal
    setTransferItem(item);
    setOpenTransferModal(true);
  };

  const confirmTransfer = async () => {
    if (!transferItem) return;

    try {
      setTransferringItemId(transferItem.id);
      setError(null);

      await httpRequest(`invalid-data/${transferItem.id}/transfer`, {
        method: 'POST',
        body: {},
      });

      setOpenTransferModal(false);

      // Show appropriate success message based on duplicate status
      const isDuplicateSent = transferItem?.error_message === 'duplicate_sent';
      const isDuplicateFree = transferItem?.error_message === 'duplicate_free';

      if (isDuplicateSent) {
        setSuccessMessage(
          'Data transferred successfully to duplicate sent data table for review!',
        );
      } else if (isDuplicateFree) {
        setSuccessMessage(
          'Data transferred successfully! Existing free data has been replaced.',
        );
      } else {
        setSuccessMessage('Data transferred successfully to free data table!');
      }

      setTransferItem(null);
      setOpenSuccessModal(true);

      // Refresh data
      await fetchInvalidData(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to transfer data';
      setError(`Transfer failed: ${errorMessage}`);
      console.error('Error transferring data:', err);

      // Show validation errors in modal
      setValidationResult({
        isValid: false,
        errors: [errorMessage],
      });
      setOpenValidationModal(true);
    } finally {
      setTransferringItemId(null);
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteItemId) {
      try {
        await httpRequest(`invalid-data/${deleteItemId}`, {
          method: 'DELETE',
        });
        fetchInvalidData(true);
        setOpenDeleteModal(false);
        setDeleteItemId(null);
      } catch (err) {
        setError('Failed to delete invalid data');
        console.error('Error deleting invalid data:', err);
        setOpenDeleteModal(false);
        setDeleteItemId(null);
      }
    }
  };

  const handlePageChange = (event, newPage) => {
    const pageIndex = newPage - 1; // Convert from 1-based to 0-based
    setPagination((prev) => ({...prev, page: pageIndex}));
    // Apply current filters with new page
    applyFiltersWithPage(pageIndex);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination((prev) => ({...prev, pageSize: newPageSize, page: 0}));
    // Apply current filters with new page size
    applyFiltersWithPage(0, newPageSize);
  };

  const applyFiltersWithPage = async (page, pageSize = pagination.pageSize) => {
    setLoadingFilters(true);
    try {
      const filterParams = {};

      if (filters.search) filterParams.search = filters.search;
      if (filters.reason) filterParams.reason = filters.reason;
      if (filters.source) filterParams.source = filters.source;
      if (filters.data_source) filterParams.data_source = filters.data_source;
      const normalizeToDayIso = (d, isEndOfDay = false) => {
        if (!d) return '';
        try {
          const dd = d instanceof Date ? new Date(d) : new Date(`${d}T00:00:00`);
          if (isNaN(dd.getTime())) return '';
          if (isEndOfDay) {
            dd.setHours(23, 59, 59, 999);
          } else {
            dd.setHours(0, 0, 0, 0);
          }
          return dd.toISOString();
        } catch (_) {
          return '';
        }
      };
      if (filters.date_from || filters.date_to) {
        const fromIso = normalizeToDayIso(filters.date_from, false);
        const toIso = normalizeToDayIso(filters.date_to, true);
        if (fromIso) filterParams.date_from = fromIso;
        if (toIso) filterParams.date_to = toIso;
      }
      if (filters.age_min) filterParams.age_min = filters.age_min;
      if (filters.age_max) filterParams.age_max = filters.age_max;
      if (filters.country) filterParams.country = filters.country;
      if (filters.funnel_name) filterParams.funnel_name = filters.funnel_name;

      await fetchInvalidData(false, page, pageSize, filterParams);
    } catch (error) {
      console.error('Error applying filters with page:', error);
    } finally {
      setLoadingFilters(false);
    }
  };

  const getReasonColor = (reason) => {
    if (!reason) return 'default';
    if (reason === 'duplicate_free') return 'warning';
    if (reason === 'duplicate_sent') return 'error';
    if (
      reason?.includes('✅ Data validated successfully') ||
      reason?.includes('Data has been fixed and is ready for transfer') ||
      reason?.includes('Data has been validated and is ready for transfer')
    )
      return 'success';
    if (
      reason?.includes('❌ Validation failed') ||
      reason?.includes('Validation failed')
    )
      return 'error';
    if (reason?.includes('Multiple errors') || reason?.includes('Multi errors'))
      return 'error';
    if (reason?.includes('❌') && reason?.includes('Email')) return 'error';
    if (reason?.includes('❌') && reason?.includes('Phone')) return 'warning';
    if (reason?.includes('❌') && reason?.includes('required')) return 'info';
    if (reason?.includes('⚠️') || reason?.includes('warning')) return 'warning';
    return 'default';
  };

  // Helper function to get all validation errors for an item
  const getAllValidationErrors = (item) => {
    // Try to use validation_errors from backend if available
    let errors = [];
    if (item.validation_errors && typeof item.validation_errors === 'string') {
      try {
        errors = JSON.parse(item.validation_errors);
      } catch (e) {
        // If parsing fails, continue with manual validation
      }
    } else if (Array.isArray(item.validation_errors)) {
      errors = item.validation_errors;
    }

    // If no backend validation errors, check manually
    if (errors.length === 0) {
      const rawData = item.raw_data || {};

      if (!rawData.fullName && !rawData.name) {
        errors.push('Name is required');
      }
      if (!rawData.email) {
        errors.push('Email is required');
      } else if (
        rawData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawData.email)
      ) {
        errors.push('Valid email format is required');
      }
      if (!rawData.phone) {
        errors.push('Phone is required');
      } else if (rawData.phone && rawData.phone.replace(/\D/g, '').length < 7) {
        errors.push('Valid phone format is required');
      }
      if (!rawData.country) {
        errors.push('Country is required');
      }
      if (!rawData.MPC_4) {
        errors.push('Funnel name is required');
      }
    }

    return errors;
  };

  const formatErrorReason = (item) => {
    if (item.error_message === 'duplicate_free') {
      const count = item.duplicate_count || item.count || 1;
      return count > 1
        ? `Duplicate in Free Data (${count} occurrences)`
        : 'Duplicate in Free Data';
    }
    if (item.error_message === 'duplicate_sent') {
      const count = item.duplicate_count || item.count || 1;
      return count > 1
        ? `Duplicate in Sent Data (${count} times sent)`
        : 'Duplicate in Sent Data';
    }
    // Handle the case where error_message is just "unique" (legacy data)
    if (item.error_message === 'unique') {
      return '✅ Data validated successfully - Ready for transfer';
    }
    // Check if data has been fixed or validated
    if (
      item.error_message?.includes('✅ Data validated successfully') ||
      item.error_message?.includes(
        'Data has been fixed and is ready for transfer',
      ) ||
      item.error_message?.includes(
        'Data has been validated and is ready for transfer',
      )
    ) {
      return item.error_message;
    }

    // Check if validation failed
    if (
      item.error_message?.includes('❌ Validation failed') ||
      item.error_message?.includes('Validation failed')
    ) {
      return item.error_message;
    }

    // Get all validation errors using the helper function
    const errors = getAllValidationErrors(item);

    // If there are multiple errors, show the first one with a count
    if (errors.length > 1) {
      return `${errors[0]} (+${errors.length - 1} more)`;
    }

    // If there's only one error, show the specific error
    if (errors.length === 1) {
      return errors[0];
    }

    // If no validation errors found, show the stored error message
    return item.error_message || 'Unknown error';
  };


  // Skeleton loading component - only for data table
  const SkeletonTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Skeleton variant='text' width={40} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={30} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={60} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={60} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={40} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={60} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={100} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={60} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={80} />
            </TableCell>
            <TableCell>
              <Skeleton variant='text' width={80} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({length: 8}).map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton variant='text' width={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={30} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={120} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={150} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={100} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={60} />
              </TableCell>
              <TableCell>
                <Skeleton variant='rectangular' width={120} height={24} />
              </TableCell>
              <TableCell>
                <Skeleton variant='rectangular' width={80} height={24} />
              </TableCell>
              <TableCell>
                <Skeleton variant='text' width={80} />
              </TableCell>
              <TableCell>
                <Box display='flex' gap={1}>
                  <Skeleton variant='circular' width={32} height={32} />
                  <Skeleton variant='circular' width={32} height={32} />
                  <Skeleton variant='circular' width={32} height={32} />
                  <Skeleton variant='circular' width={32} height={32} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h4' component='h1'>
          Incomplete Data Management
        </Typography>
        <Button
          variant='outlined'
          startIcon={
            refreshing ? <CircularProgress size={20} /> : <RefreshIcon />
          }
          onClick={() => fetchInvalidData(true)}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{mb: 2}} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Field - Full Width */}
      <Card sx={{mb: 3}}>
        <CardContent>
          <TextField
            fullWidth
            label='Search (Name, Email, Phone, ID, or Other Data content)'
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{mr: 1, color: 'text.secondary'}} />
              ),
            }}
            placeholder='Search by name, email, phone, ID, or other data content...'
          />
        </CardContent>
      </Card>

      {/* Advanced Filters - Collapsed by default */}
      <Card sx={{mb: 3}}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{cursor: 'pointer'}}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Typography variant='h6'>
              Advanced Filters
              {!filtersExpanded && (
                <Chip
                  label={`${
                    Object.values(filters).filter(
                      (v) => v && v !== '' && v !== filters.search,
                    ).length
                  } active`}
                  size='small'
                  color='primary'
                  sx={{ml: 1}}
                />
              )}
            </Typography>
            <IconButton size='small'>
              {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          {filtersExpanded && (
            <Grid container spacing={2} sx={{mt: 1}}>
              {/* Reason Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Invalid Reason</InputLabel>
                  <Select
                    value={filters.reason}
                    onChange={(e) =>
                      setFilters({...filters, reason: e.target.value})
                    }
                  >
                    <MenuItem value=''>All Reasons</MenuItem>
                    {filterOptions.reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Source Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Integration Source'
                  value={filters.source}
                  onChange={(e) =>
                    setFilters({...filters, source: e.target.value})
                  }
                  placeholder='Enter integration source'
                />
              </Grid>

              {/* Data Source Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Data Source'
                  value={filters.data_source}
                  onChange={(e) =>
                    setFilters({...filters, data_source: e.target.value})
                  }
                  placeholder='Enter data source'
                />
              </Grid>


              {/* Country Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Country'
                  value={filters.country}
                  onChange={(e) =>
                    setFilters({...filters, country: e.target.value})
                  }
                  placeholder='Enter country name'
                />
              </Grid>

              {/* Funnel Name Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Funnel Name'
                  value={filters.funnel_name}
                  onChange={(e) =>
                    setFilters({...filters, funnel_name: e.target.value})
                  }
                  placeholder='Enter funnel name'
                />
              </Grid>

              {/* Age Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Min Age'
                  type='number'
                  value={filters.age_min}
                  onChange={(e) =>
                    setFilters({...filters, age_min: e.target.value})
                  }
                  inputProps={{min: 0, max: 120}}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Max Age'
                  type='number'
                  value={filters.age_max}
                  onChange={(e) =>
                    setFilters({...filters, age_max: e.target.value})
                  }
                  inputProps={{min: 0, max: 120}}
                />
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='From Date'
                  type='date'
                  value={filters.date_from}
                  onChange={(e) =>
                    setFilters({...filters, date_from: e.target.value})
                  }
                  InputLabelProps={{shrink: true}}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='To Date'
                  type='date'
                  value={filters.date_to}
                  onChange={(e) =>
                    setFilters({...filters, date_to: e.target.value})
                  }
                  InputLabelProps={{shrink: true}}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display='flex' gap={2} justifyContent='center'>
                  <Button
                    variant='contained'
                    onClick={applyFilters}
                    disabled={loadingFilters}
                    startIcon={
                      loadingFilters ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    sx={{minWidth: 150}}
                  >
                    {loadingFilters ? 'Filtering...' : 'Apply Filters'}
                  </Button>

                  <Button
                    variant='outlined'
                    onClick={() => {
                      setFilters({
                        search: '',
                        reason: '',
                        source: '',
                        data_source: '',
                        date_from: '',
                        date_to: '',
                        age_min: '',
                        age_max: '',
                        country: '',
                        funnel_name: '',
                      });
                      // Apply the cleared filters immediately
                      setTimeout(() => applyFilters(), 100);
                    }}
                    sx={{minWidth: 150}}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6'>
              Incomplete Data Entries ({pagination.total})
            </Typography>
          </Box>

          {invalidData.length === 0 ? (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              py={8}
              px={4}
            >
              <CheckCircle
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant='h5' color='text.secondary' gutterBottom>
                No Incomplete Data
              </Typography>
              <Typography
                variant='body1'
                color='text.secondary'
                textAlign='center'
                maxWidth={400}
              >
                Great! There are currently no incomplete data entries. All
                incoming data is properly formatted and validated.
              </Typography>
              <Button
                variant='outlined'
                startIcon={<RefreshIcon />}
                onClick={() => fetchInvalidData(true)}
                sx={{mt: 3}}
              >
                Refresh Data
              </Button>
            </Box>
          ) : (
            <>
              {selectedItems.length > 0 && (
                <Card sx={{mb: 2, backgroundColor: '#e3f2fd'}}>
                  <CardContent sx={{py: 1}}>
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                        {selectedItems.length} item(s) selected
                      </Typography>
                      <Box display='flex' gap={1}>
                        <Button
                          variant='contained'
                          color='success'
                          onClick={handleBulkTransferClick}
                          disabled={bulkTransferring}
                          startIcon={
                            bulkTransferring ? (
                              <CircularProgress size={16} />
                            ) : (
                              <TransferIcon />
                            )
                          }
                        >
                          {bulkTransferring
                            ? 'Transferring...'
                            : 'Transfer Selected'}
                        </Button>
                        <Button
                          variant='outlined'
                          onClick={() => setSelectedItems([])}
                        >
                          Clear Selection
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {loading ? (
                <SkeletonTable />
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{minWidth: 150}}>Actions</TableCell>
                        <TableCell padding='checkbox' sx={{width: 48}} />
                        <TableCell sx={{width: 80}}>ID</TableCell>
                        <TableCell sx={{minWidth: 180}}>Name</TableCell>
                        <TableCell sx={{minWidth: 220}}>Email</TableCell>
                        <TableCell sx={{minWidth: 180}}>Phone</TableCell>
                        <TableCell sx={{width: 80}}>Age</TableCell>
                        <TableCell sx={{minWidth: 140}}>Country</TableCell>
                        <TableCell sx={{minWidth: 200}}>
                          Invalid Reason
                        </TableCell>
                        <TableCell sx={{minWidth: 140}}>Source</TableCell>
                        <TableCell sx={{minWidth: 140}}>Data Source</TableCell>
                        <TableCell sx={{minWidth: 120}}>Other Data</TableCell>
                        <TableCell sx={{minWidth: 120}}>Received</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invalidData.map((item) => {
                        const rawData = item.raw_data || {};
                        const isGrouped = item.isGrouped && item.count > 1;
                        return (
                          <TableRow
                            key={
                              item.isGrouped ? `grouped-${item.id}` : item.id
                            }
                          >
                            {/* Actions first */}
                            <TableCell>
                              <Box display='flex' gap={1}>
                                <Tooltip title='Validate Data'>
                                  <IconButton
                                    size='small'
                                    onClick={() => handleValidate(item)}
                                    color='info'
                                  >
                                    {validatingItemId === item.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <ValidateIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    isGrouped
                                      ? 'Cannot edit grouped items'
                                      : 'Edit'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size='small'
                                      onClick={() => handleEdit(item)}
                                      disabled={
                                        isGrouped ||
                                        updatingItemId === item.id ||
                                        transferringItemId === item.id
                                      }
                                    >
                                      {updatingItemId === item.id ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <EditIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    isGrouped
                                      ? 'Cannot transfer grouped items'
                                      : 'Transfer to Main Data'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size='small'
                                      onClick={() => handleTransfer(item.id)}
                                      color='success'
                                      disabled={
                                        isGrouped ||
                                        updatingItemId === item.id ||
                                        transferringItemId === item.id
                                      }
                                    >
                                      {transferringItemId === item.id ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <TransferIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title='Delete'>
                                  <IconButton
                                    size='small'
                                    onClick={() => handleDelete(item.id)}
                                    color='error'
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>

                            {/* Selection checkbox second */}
                            <TableCell padding='checkbox'>
                              {canItemBeTransferred(item) && (
                                <input
                                  type='checkbox'
                                  checked={isItemSelected(item.id)}
                                  onChange={() => handleSelectItem(item.id)}
                                  style={{cursor: 'pointer'}}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant='body2'
                                sx={{fontWeight: 'bold', color: 'primary.main'}}
                              >
                                #{item.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display='flex' alignItems='center' gap={1}>
                                <Typography variant='subtitle2'>
                                  {rawData.fullName || rawData.name || 'N/A'}
                                </Typography>
                                {isGrouped && (
                                  <Chip
                                    label={`${item.count}x`}
                                    size='small'
                                    color='warning'
                                    sx={{height: 20, fontSize: '0.7rem'}}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {rawData.email || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {rawData.phone || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {rawData.age || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {rawData.country || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={(() => {
                                  // For duplicates, show detailed info
                                  if (item.error_message === 'duplicate_free') {
                                    const count =
                                      item.duplicate_count || item.count || 1;
                                    return `This data already exists in the free data table (${count} occurrence${
                                      count > 1 ? 's' : ''
                                    })`;
                                  }
                                  if (item.error_message === 'duplicate_sent') {
                                    const count =
                                      item.duplicate_count || item.count || 1;
                                    return `This data was already sent to ${count} customer${
                                      count > 1 ? 's' : ''
                                    } and cannot be transferred to free data`;
                                  }
                                  // For other errors, show validation details
                                  const errors = getAllValidationErrors(item);
                                  return errors.length > 0
                                    ? errors.join(' • ')
                                    : formatErrorReason(item);
                                })()}
                                arrow
                              >
                                <Chip
                                  label={formatErrorReason(item)}
                                  color={getReasonColor(
                                    formatErrorReason(item),
                                  )}
                                  size='small'
                                  sx={{
                                    maxWidth: 300,
                                    height: 'auto',
                                    minHeight: 24,
                                    '& .MuiChip-label': {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      padding: '4px 8px',
                                      display: 'block',
                                    },
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.integration_name || 'Unknown'}
                                color='primary'
                                size='small'
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {rawData.data_source || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {rawData.other_data ? (
                                <Button
                                  size='small'
                                  variant='outlined'
                                  onClick={() => {
                                    setSelectedOtherData(rawData.other_data);
                                    setOpenOtherDataModal(true);
                                  }}
                                >
                                  Show
                                </Button>
                              ) : (
                                <Typography variant='body2' color='text.secondary'>
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2'>
                                {new Date(item.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Pagination Controls */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                  px: 2,
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                  <Typography variant='body2' color='text.secondary'>
                    Showing {pagination.page * pagination.pageSize + 1} to{' '}
                    {Math.min(
                      (pagination.page + 1) * pagination.pageSize,
                      pagination.total,
                    )}{' '}
                    of {pagination.total} entries
                  </Typography>
                  <FormControl size='small' sx={{minWidth: 120}}>
                    <Select
                      value={pagination.pageSize}
                      onChange={handlePageSizeChange}
                      disabled={loadingFilters}
                      displayEmpty
                    >
                      <MenuItem value={5}>5 per page</MenuItem>
                      <MenuItem value={10}>10 per page</MenuItem>
                      <MenuItem value={25}>25 per page</MenuItem>
                      <MenuItem value={50}>50 per page</MenuItem>
                      <MenuItem value={100}>100 per page</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page + 1}
                  onChange={handlePageChange}
                  color='primary'
                  disabled={loadingFilters}
                  showFirstButton
                  showLastButton
                  siblingCount={2}
                  boundaryCount={1}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Edit Incomplete Data Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{mt: 1}}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Name'
                value={formData.name}
                onChange={(e) =>
                  setFormData({...formData, name: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Email'
                value={formData.email}
                onChange={(e) =>
                  setFormData({...formData, email: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Phone'
                value={formData.phone}
                onChange={(e) =>
                  setFormData({...formData, phone: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Age'
                value={formData.age}
                onChange={(e) =>
                  setFormData({...formData, age: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Country'
                value={formData.country}
                onChange={(e) =>
                  setFormData({...formData, country: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label='Marks'
                value={formData.marks}
                onChange={(e) =>
                  setFormData({...formData, marks: e.target.value})
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Funnel Name (Lead Source)'
                value={formData.MPC_4}
                onChange={(e) =>
                  setFormData({...formData, MPC_4: e.target.value})
                }
                helperText='Enter the funnel/source where this lead came from'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Data Source'
                value={formData.data_source}
                onChange={(e) =>
                  setFormData({...formData, data_source: e.target.value})
                }
                helperText='Enter the data source (e.g., Website, API, Import)'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Other Data'
                value={formData.other_data || ''}
                onChange={(e) =>
                  setFormData({...formData, other_data: e.target.value})
                }
                multiline
                rows={3}
                helperText='Additional data in JSON format or plain text'
              />
            </Grid>

            {/* Validation Preview */}
            <Grid item xs={12}>
              <Box sx={{mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1}}>
                <Typography variant='subtitle2' gutterBottom>
                  Validation Preview:
                </Typography>
                {(() => {
                  const updatedData = {
                    ...editingItem,
                    raw_data: {
                      ...editingItem?.raw_data,
                      ...formData,
                    },
                  };
                  const validation = validateData(updatedData);

                  if (validation.isValid) {
                    return (
                      <Alert severity='success' sx={{mt: 1}}>
                        <Typography variant='body2'>
                          ✅ Data is valid! Will be automatically transferred to
                          free data table after saving.
                        </Typography>
                      </Alert>
                    );
                  } else {
                    return (
                      <Alert severity='warning' sx={{mt: 1}}>
                        <Typography variant='body2'>
                          ⚠️ Data still has validation errors. Will remain in
                          invalid data list.
                        </Typography>
                        <Box component='ul' sx={{mt: 1, pl: 2}}>
                          {validation.errors.map((error, index) => (
                            <li key={index}>
                              <Typography variant='caption' color='error'>
                                {error}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      </Alert>
                    );
                  }
                })()}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenEditDialog(false)}
            disabled={updatingItemId === editingItem?.id}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant='contained'
            disabled={updatingItemId === editingItem?.id}
            startIcon={
              updatingItemId === editingItem?.id ? (
                <CircularProgress size={20} />
              ) : null
            }
          >
            {updatingItemId === editingItem?.id
              ? 'Saving...'
              : (() => {
                  const updatedData = {
                    ...editingItem,
                    raw_data: {
                      ...editingItem?.raw_data,
                      ...formData,
                    },
                  };
                  const validation = validateData(updatedData);
                  return validation.isValid
                    ? 'Save & Transfer'
                    : 'Save Changes';
                })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Modal */}
      <Dialog
        open={openValidationModal}
        onClose={() => setOpenValidationModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            {validationPhase !== 'done' ? (
              <>
                <CircularProgress size={20} />
                <Typography variant='h6'>
                  {validationPhase === 'validating' && 'Validating fields…'}
                  {validationPhase === 'checking_duplicate' &&
                    'Checking duplicates…'}
                </Typography>
              </>
            ) : validationResult.isValid ? (
              <>
                <CheckCircle color='success' />
                <Typography variant='h6' color='success.main'>
                  {validationResult.warnings?.length > 0
                    ? 'Validation Successful with Warnings'
                    : 'Validation Successful'}
                </Typography>
              </>
            ) : (
              <>
                <Error color='error' />
                <Typography variant='h6' color='error.main'>
                  Validation Failed
                </Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {validationPhase !== 'done' ? (
            <Box>
              <Alert severity='info' sx={{mb: 2}}>
                <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                  {validationPhase === 'validating' && 'Validating fields…'}
                  {validationPhase === 'checking_duplicate' &&
                    'Checking duplicates…'}
                </Typography>
              </Alert>
            </Box>
          ) : validationResult.isValid ? (
            <Box>
              <Alert severity='success' sx={{mb: 2}}>
                <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                  ✅ Data is valid and ready for transfer!
                </Typography>
              </Alert>
              {validationResult.warnings?.length > 0 && (
                <Alert severity='warning' sx={{mb: 2}}>
                  <Typography variant='body2' sx={{fontWeight: 'bold', mb: 1}}>
                    ⚠️ Please review the following warnings:
                  </Typography>
                  <Box component='ul' sx={{pl: 2, m: 0}}>
                    {validationResult.warnings.map((warning, index) => (
                      <Typography
                        key={index}
                        component='li'
                        variant='body2'
                        sx={{mb: 0.5}}
                      >
                        {warning}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity='error' sx={{mb: 2}}>
                <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                  ❌ Please fix the following errors before transferring:
                </Typography>
              </Alert>
              <Box component='ul' sx={{pl: 2, m: 0, mb: 2}}>
                {validationResult.errors.map((error, index) => (
                  <Typography
                    key={index}
                    component='li'
                    variant='body2'
                    sx={{mb: 1}}
                  >
                    {error}
                  </Typography>
                ))}
              </Box>
              {validationResult.warnings?.length > 0 && (
                <Alert severity='warning'>
                  <Typography variant='body2' sx={{fontWeight: 'bold', mb: 1}}>
                    ⚠️ Additional warnings to review:
                  </Typography>
                  <Box component='ul' sx={{pl: 2, m: 0}}>
                    {validationResult.warnings.map((warning, index) => (
                      <Typography
                        key={index}
                        component='li'
                        variant='body2'
                        sx={{mb: 0.5}}
                      >
                        {warning}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenValidationModal(false)}>
            {validationResult.isValid ? 'Close' : 'Edit Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <CheckCircle color='success' />
            <Typography variant='h6' color='success.main'>
              Transfer Successful
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='success' sx={{mt: 1}}>
            <Typography variant='body1'>{successMessage}</Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenSuccessModal(false)}
            variant='contained'
            color='success'
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Confirmation Modal */}
      <Dialog
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            {transferItem?.error_message === 'duplicate_sent' ? (
              <>
                <Error color='warning' />
                <Typography variant='h6' color='warning.main'>
                  Duplicate Detected - Will Transfer to Duplicate Sent Data
                </Typography>
              </>
            ) : (
              <>
                <TransferIcon color='success' />
                <Typography variant='h6' color='success.main'>
                  Confirm Transfer to Free Data Table
                </Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {transferItem?.error_message === 'duplicate_sent' ? (
            <>
              <Alert severity='warning' sx={{mb: 2}}>
                <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                  ⚠️ This data is a duplicate of already sent data
                </Typography>
              </Alert>
              <Typography variant='body1' sx={{mb: 2}}>
                Since this data matches existing data that has already been sent
                to customers, it will be transferred to the{' '}
                <strong>Duplicate Sent Data</strong> table instead of the free
                data table. This will:
              </Typography>
              <Box component='ul' sx={{pl: 2, mb: 2}}>
                <li>Validate all required fields</li>
                <li>Move the data to the duplicate sent data table</li>
                <li>Remove it from the invalid data list</li>
                <li>Mark it for manual review before sending</li>
              </Box>
            </>
          ) : transferItem?.error_message === 'duplicate_free' ? (
            <>
              <Alert severity='info' sx={{mb: 2}}>
                <Typography variant='body1' sx={{fontWeight: 'bold'}}>
                  ℹ️ This data is a duplicate of existing free data
                </Typography>
              </Alert>
              <Typography variant='body1' sx={{mb: 2}}>
                Since this data matches existing free data, it will replace the
                old record. This will:
              </Typography>
              <Box component='ul' sx={{pl: 2, mb: 2}}>
                <li>Validate all required fields</li>
                <li>
                  Replace the existing free data with this updated version
                </li>
                <li>Remove it from the invalid data list</li>
                <li>Make it available for sending to customers</li>
              </Box>
            </>
          ) : (
            <>
              <Typography variant='body1' sx={{mb: 2}}>
                Are you sure you want to transfer this data to the free data
                table? This will:
              </Typography>
              <Box component='ul' sx={{pl: 2, mb: 2}}>
                <li>Validate all required fields</li>
                <li>Move the data to the main data table</li>
                <li>Remove it from the invalid data list</li>
                <li>Make it available for sending to customers</li>
              </Box>
            </>
          )}
          {transferItem && (
            <Box sx={{p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2}}>
              <Typography variant='subtitle2' gutterBottom>
                Data Preview:
              </Typography>
              <Typography variant='body2'>
                <strong>Name:</strong>{' '}
                {transferItem.raw_data?.fullName ||
                  transferItem.raw_data?.name ||
                  'N/A'}
                <br />
                <strong>Email:</strong> {transferItem.raw_data?.email || 'N/A'}
                <br />
                <strong>Phone:</strong> {transferItem.raw_data?.phone || 'N/A'}
                <br />
                <strong>Age:</strong> {transferItem.raw_data?.age || 'N/A'}
                <br />
                <strong>Country:</strong>{' '}
                {transferItem.raw_data?.country || 'N/A'}
                <br />
                <strong>Marks:</strong> {transferItem.raw_data?.marks || 'N/A'}
                <br />
                <strong>Funnel Name:</strong>{' '}
                {transferItem.raw_data?.MPC_4 || 'N/A'}
                <br />
                <strong>Data Source:</strong>{' '}
                {transferItem.raw_data?.data_source || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenTransferModal(false);
              setTransferItem(null);
              // Data remains in invalid list but with "fixed" status
            }}
            disabled={transferringItemId === transferItem?.id}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmTransfer}
            variant='contained'
            color='success'
            disabled={transferringItemId === transferItem?.id}
            startIcon={
              transferringItemId === transferItem?.id ? (
                <CircularProgress size={20} />
              ) : null
            }
          >
            {transferringItemId === transferItem?.id
              ? 'Transferring...'
              : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <Error color='error' />
            <Typography variant='h6' color='error.main'>
              Confirm Delete
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='warning' sx={{mt: 1}}>
            <Typography variant='body1'>
              Are you sure you want to delete this incomplete data entry? This
              action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Errors Modal */}
      <Dialog
        open={openValidationModal}
        onClose={() => setOpenValidationModal(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <Error color='error' />
            <Typography variant='h6'>Validation Errors</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='error' sx={{mb: 2}}>
            <Typography variant='body1' sx={{fontWeight: 'bold'}}>
              ❌ Cannot transfer selected items due to validation errors
            </Typography>
          </Alert>

          <Typography variant='body2' sx={{mb: 2}}>
            The following items have validation errors and cannot be
            transferred:
          </Typography>

          {validationErrors.map((error, index) => (
            <Card key={index} variant='outlined' sx={{mb: 2, p: 2}}>
              <Typography variant='subtitle2' sx={{fontWeight: 'bold', mb: 1}}>
                Item ID: {error.id}
              </Typography>
              <Box component='ul' sx={{pl: 2, m: 0}}>
                {error.errors.map((err, errIndex) => (
                  <Typography
                    key={errIndex}
                    component='li'
                    variant='body2'
                    sx={{mb: 0.5, color: 'error.main'}}
                  >
                    {err}
                  </Typography>
                ))}
              </Box>
            </Card>
          ))}

          <Alert severity='info' sx={{mt: 2}}>
            <Typography variant='body2'>
              Please fix the validation errors for these items before attempting
              to transfer them. You can edit each item individually to resolve
              the issues.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenValidationModal(false)}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Transfer Confirmation Modal */}
      <Dialog
        open={openBulkTransferModal}
        onClose={() => setOpenBulkTransferModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <TransferIcon color='success' />
            <Typography variant='h6'>Confirm Bulk Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity='info' sx={{mb: 2}}>
            <Typography variant='body1' sx={{fontWeight: 'bold'}}>
              📋 Ready to Transfer {selectedItems.length} Item(s)
            </Typography>
          </Alert>

          <Typography variant='body1' sx={{mb: 2}}>
            You are about to transfer <strong>{selectedItems.length}</strong>{' '}
            validated item(s) to the main data table.
          </Typography>

          <Typography variant='body2' sx={{mb: 2}}>
            This action will:
          </Typography>

          <Box component='ul' sx={{pl: 2, m: 0, mb: 2}}>
            <Typography component='li' variant='body2' sx={{mb: 0.5}}>
              Move selected items from Incomplete Data to the main data table
            </Typography>
            <Typography component='li' variant='body2' sx={{mb: 0.5}}>
              Remove them from the Incomplete Data list
            </Typography>
            <Typography component='li' variant='body2' sx={{mb: 0.5}}>
              Make them available for normal data operations
            </Typography>
          </Box>

          <Alert severity='warning' sx={{mt: 2}}>
            <Typography variant='body2'>
              ⚠️ This action cannot be undone. Please ensure all selected items
              are properly validated.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenBulkTransferModal(false)}
            disabled={bulkTransferring}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmBulkTransfer}
            variant='contained'
            color='success'
            disabled={bulkTransferring}
            startIcon={
              bulkTransferring ? (
                <CircularProgress size={20} />
              ) : (
                <TransferIcon />
              )
            }
          >
            {bulkTransferring ? 'Transferring...' : 'Confirm Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Other Data Modal */}
      <Dialog
        open={openOtherDataModal}
        onClose={() => {
          setOpenOtherDataModal(false);
          setSelectedOtherData(null);
        }}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <Typography variant='h6'>Other Data</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{mt: 2}}>
            {selectedOtherData ? (
              <TextField
                fullWidth
                multiline
                rows={15}
                value={
                  typeof selectedOtherData === 'object'
                    ? JSON.stringify(selectedOtherData, null, 2)
                    : String(selectedOtherData)
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No other data available
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenOtherDataModal(false);
              setSelectedOtherData(null);
            }}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvalidDataList;
